// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — Configurações / Importação de Dados (Trilha P1b)
//
//  Wizard de importação de planilhas (CSV/XLSX): enviar arquivo → mapear
//  colunas (com sugestão automática + presets Bling/Conta Azul/Tiny) →
//  validar (relatório de erros/avisos por linha) → carregar (idempotente).
//  Aba Histórico lista os jobs com contadores e download do CSV de ocorrências.
//  Plano: docs/importacao/PLANO_PLATAFORMA_IMPORTACAO.md
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useRef, useState } from "react";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";
import { usePaginacaoServidor } from "@/hooks/usePaginacaoServidor";
import type { PaginadoResponse } from "@/lib/consultas";
import { toast } from "sonner";
import {
  AlertTriangle, ArrowLeft, CheckCircle2, Download, FileSpreadsheet,
  Loader2, Upload, XCircle,
} from "lucide-react";

import { apiClient } from "@/lib/apiClient";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// ── Tipos (espelham os DTOs do ImportacaoController) ─────────────────────────

interface CampoDestino { chave: string; rotulo: string; obrigatorio: boolean; descricao?: string | null }
interface EntidadeInfo { entidade: string; campos: CampoDestino[]; presets: { chave: string; nome: string }[] }
interface MapeamentoColuna { coluna: string; campo: string }
interface UploadResp {
  jobId: number; entidade: string; colunas: string[]; amostra: (string | null)[][];
  totalLinhas: number; mapeamentoSugerido: MapeamentoColuna[]; preset: string | null;
}
interface Ocorrencia {
  linha: number; coluna: string | null; severidade: number;
  codigo: string; mensagem: string; valorBruto: string | null;
}
interface ValidacaoResp {
  jobId: number; status: string; totalLinhas: number;
  linhasValidas: number; linhasComErro: number; avisos: number; ocorrencias: Ocorrencia[];
}
interface CargaResp {
  jobId: number; status: string;
  inseridos: number; atualizados: number; pulados: number; rejeitados: number; avisos: number;
}
interface JobResumo {
  id: number; entidade: string; nomeArquivo: string; status: string; preset: string | null;
  totalLinhas: number; inseridos: number; atualizados: number; pulados: number;
  rejeitados: number; avisos: number; dtCriacao: string; dtConclusao: string | null;
}

const IGNORAR = "__none__"; // sentinela — Radix Select não aceita value=""
const TODOS = "__todos__";

const ROTULO_ENTIDADE: Record<string, string> = {
  Produto: "Produtos",
  Pessoa: "Clientes / Fornecedores",
  SaldoEstoque: "Saldo inicial de estoque",
  FichaTecnica: "Fichas técnicas (insumos por produto)",
  ContaPagar: "Contas a pagar em aberto",
  ContaReceber: "Contas a receber em aberto",
};

const ROTULO_STATUS: Record<string, string> = {
  AguardandoMapeamento: "Aguardando mapeamento",
  Validado: "Validado",
  Carregando: "Carregando",
  Concluido: "Concluído",
  Falha: "Falha",
};

export default function ImportacaoDados() {
  const queryClient = useQueryClient();
  const inputArquivo = useRef<HTMLInputElement>(null);

  // wizard
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [entidade, setEntidade] = useState("Produto");
  const [preset, setPreset] = useState(IGNORAR);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [upload, setUpload] = useState<UploadResp | null>(null);
  const [mapeamento, setMapeamento] = useState<Record<string, string>>({});
  const [politica, setPolitica] = useState<"Pular" | "Atualizar">("Pular");
  const [validando, setValidando] = useState(false);
  const [validacao, setValidacao] = useState<ValidacaoResp | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [carga, setCarga] = useState<CargaResp | null>(null);

  const { data: entidades } = useQuery({
    queryKey: ["importacao-entidades"],
    queryFn: async () => (await apiClient.get<EntidadeInfo[]>("/api/importacoes/entidades")).data,
  });

  const [filtroEntidade, setFiltroEntidade] = useState(TODOS);
  const [filtroStatus, setFiltroStatus] = useState(TODOS);
  const pagHistorico = usePaginacaoServidor({ resetarQuando: [filtroEntidade, filtroStatus] });
  const { data: historicoResp, isFetching: carregandoHistorico } = useQuery({
    queryKey: ["importacao-historico", filtroEntidade, filtroStatus, pagHistorico.pagina, pagHistorico.tamanhoPagina],
    queryFn: async () =>
      (await apiClient.get<PaginadoResponse<JobResumo>>("/api/importacoes", {
        params: {
          entidade: filtroEntidade === TODOS ? undefined : filtroEntidade,
          status: filtroStatus === TODOS ? undefined : filtroStatus,
          pagina: pagHistorico.pagina,
          tamanhoPagina: pagHistorico.tamanhoPagina,
        },
      })).data,
    placeholderData: keepPreviousData,
  });
  const historico = historicoResp?.itens;
  const filtrandoHistorico = filtroEntidade !== TODOS || filtroStatus !== TODOS;

  const infoEntidade = useMemo(
    () => entidades?.find((e) => e.entidade === entidade),
    [entidades, entidade],
  );

  const camposPorChave = useMemo(() => {
    const mapa: Record<string, CampoDestino> = {};
    infoEntidade?.campos.forEach((c) => { mapa[c.chave] = c; });
    return mapa;
  }, [infoEntidade]);

  // ── Ações ──────────────────────────────────────────────────────────────────

  const reiniciar = () => {
    setStep(1); setArquivo(null); setUpload(null); setMapeamento({});
    setValidacao(null); setCarga(null); setPoliticaEValida("Pular");
    if (inputArquivo.current) inputArquivo.current.value = "";
    queryClient.invalidateQueries({ queryKey: ["importacao-historico"] });
  };
  const setPoliticaEValida = (p: "Pular" | "Atualizar") => setPolitica(p);

  const enviarArquivo = async () => {
    if (!arquivo) { toast.error("Selecione um arquivo CSV ou XLSX."); return; }
    setEnviando(true);
    try {
      const form = new FormData();
      form.append("arquivo", arquivo);
      const presetQuery = preset === IGNORAR ? "" : `&preset=${preset}`;
      const { data } = await apiClient.post<UploadResp>(
        `/api/importacoes/upload?entidade=${entidade}${presetQuery}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120_000 },
      );
      setUpload(data);
      const inicial: Record<string, string> = {};
      data.mapeamentoSugerido.forEach((m) => { inicial[m.coluna] = m.campo; });
      setMapeamento(inicial);
      setStep(2);
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao enviar o arquivo.");
    } finally {
      setEnviando(false);
    }
  };

  const validar = async () => {
    if (!upload) return;
    const lista: MapeamentoColuna[] = Object.entries(mapeamento)
      .filter(([, campo]) => campo !== IGNORAR)
      .map(([coluna, campo]) => ({ coluna, campo }));

    setValidando(true);
    try {
      const { data } = await apiClient.post<ValidacaoResp>(
        `/api/importacoes/${upload.jobId}/validar`,
        { mapeamento: lista, politicaConflito: politica },
        { timeout: 120_000 },
      );
      setValidacao(data);
      setStep(3);
    } catch (e: any) {
      toast.error(e.message ?? "Falha na validação.");
    } finally {
      setValidando(false);
    }
  };

  const carregar = async () => {
    if (!upload) return;
    setCarregando(true);
    try {
      const { data } = await apiClient.post<CargaResp>(
        `/api/importacoes/${upload.jobId}/carregar`, undefined, { timeout: 300_000 });
      setCarga(data);
      setStep(4);
      toast.success("Importação concluída.");
      queryClient.invalidateQueries({ queryKey: ["importacao-historico"] });
    } catch (e: any) {
      toast.error(e.message ?? "Falha na carga.");
    } finally {
      setCarregando(false);
    }
  };

  const baixarErros = async (jobId: number) => {
    try {
      const { data } = await apiClient.get(`/api/importacoes/${jobId}/erros.csv`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `importacao-${jobId}-ocorrencias.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: any) {
      toast.error(e.message ?? "Falha ao baixar o relatório.");
    }
  };

  // ── Blocos de UI ───────────────────────────────────────────────────────────

  const badgeSeveridade = (s: number) =>
    s === 2
      ? <Badge variant="destructive">erro</Badge>
      : <Badge variant="outline" className="border-amber-500 text-amber-600">aviso</Badge>;

  const indicadorSteps = (
    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
      {["Arquivo", "Mapeamento", "Validação", "Resultado"].map((titulo, i) => (
        <div key={titulo} className="flex items-center gap-2">
          {i > 0 && <span>›</span>}
          <span className={step === i + 1 ? "font-semibold text-foreground" : ""}>
            {i + 1}. {titulo}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6" /> Importação de Dados
        </h1>
        <p className="text-muted-foreground">
          Traga seus produtos e clientes de planilhas ou de outro sistema (Bling, Conta Azul, Tiny).
        </p>
      </div>

      <Tabs defaultValue="importar">
        <TabsList>
          <TabsTrigger value="importar">Importar</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        {/* ═══ Aba IMPORTAR — wizard ═══ */}
        <TabsContent value="importar" className="space-y-4">
          {indicadorSteps}

          {/* Passo 1 — Arquivo */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>1. Escolha o que importar e envie o arquivo</CardTitle>
                <CardDescription>
                  Formatos aceitos: .csv e .xlsx (até 20 MB / 10.000 linhas). A primeira linha deve ser o cabeçalho.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 max-w-xl">
                <div className="space-y-2">
                  <Label>Tipo de cadastro</Label>
                  <Select value={entidade} onValueChange={(v) => { setEntidade(v); setPreset(IGNORAR); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(entidades ?? []).map((e) => (
                        <SelectItem key={e.entidade} value={e.entidade}>
                          {ROTULO_ENTIDADE[e.entidade] ?? e.entidade}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Sistema de origem (opcional)</Label>
                  <Select value={preset} onValueChange={setPreset}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={IGNORAR}>Planilha genérica</SelectItem>
                      {(infoEntidade?.presets ?? []).map((p) => (
                        <SelectItem key={p.chave} value={p.chave}>{p.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Escolher o sistema de origem melhora a sugestão automática do mapeamento de colunas.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Arquivo</Label>
                  <input
                    ref={inputArquivo}
                    type="file"
                    accept=".csv,.xlsx,.txt"
                    className="block w-full text-sm file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:opacity-90"
                    onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
                  />
                </div>

                <Button onClick={enviarArquivo} disabled={enviando || !arquivo}>
                  {enviando ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                  Enviar arquivo
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Passo 2 — Mapeamento */}
          {step === 2 && upload && (
            <Card>
              <CardHeader>
                <CardTitle>2. Confira o mapeamento das colunas</CardTitle>
                <CardDescription>
                  {upload.totalLinhas} linhas de dados em “{arquivo?.name}”. Associe cada coluna da
                  planilha a um campo do Udisys — os campos sugeridos automaticamente já vêm preenchidos.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Coluna da planilha</TableHead>
                        <TableHead>Exemplo (1ª linha)</TableHead>
                        <TableHead>Campo no Udisys</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {upload.colunas.map((coluna, i) => (
                        <TableRow key={`${coluna}-${i}`}>
                          <TableCell className="font-medium">{coluna}</TableCell>
                          <TableCell className="text-muted-foreground max-w-48 truncate">
                            {upload.amostra[0]?.[i] ?? "—"}
                          </TableCell>
                          <TableCell>
                            <Select
                              value={mapeamento[coluna] ?? IGNORAR}
                              onValueChange={(v) => setMapeamento((m) => ({ ...m, [coluna]: v }))}
                            >
                              <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value={IGNORAR}>— não importar —</SelectItem>
                                {(infoEntidade?.campos ?? []).map((c) => (
                                  <SelectItem
                                    key={c.chave}
                                    value={c.chave}
                                    disabled={Object.entries(mapeamento)
                                      .some(([col, campo]) => campo === c.chave && col !== coluna)}
                                  >
                                    {c.rotulo}{c.obrigatorio ? " *" : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex items-end gap-4 flex-wrap">
                  <div className="space-y-2">
                    <Label>Se o registro já existir</Label>
                    <Select value={politica} onValueChange={(v) => setPoliticaEValida(v as "Pular" | "Atualizar")}>
                      <SelectTrigger className="w-64"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Pular">Pular (manter o cadastro atual)</SelectItem>
                        <SelectItem value="Atualizar">Atualizar com os dados da planilha</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={reiniciar}>
                      <ArrowLeft className="h-4 w-4 mr-2" /> Voltar
                    </Button>
                    <Button onClick={validar} disabled={validando}>
                      {validando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      Validar dados
                    </Button>
                  </div>
                </div>

                {(infoEntidade?.campos ?? []).filter((c) => c.obrigatorio).length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    * campo obrigatório — precisa de uma coluna mapeada.
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Passo 3 — Validação */}
          {step === 3 && validacao && (
            <Card>
              <CardHeader>
                <CardTitle>3. Resultado da validação</CardTitle>
                <CardDescription>Nenhum dado foi gravado ainda — revise e confirme a importação.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-6 flex-wrap text-sm">
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    <b>{validacao.linhasValidas}</b> linhas válidas
                  </span>
                  <span className="flex items-center gap-1">
                    <XCircle className="h-4 w-4 text-destructive" />
                    <b>{validacao.linhasComErro}</b> com erro (serão rejeitadas)
                  </span>
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <b>{validacao.avisos}</b> avisos
                  </span>
                </div>

                {validacao.ocorrencias.length > 0 && (
                  <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-16">Linha</TableHead>
                          <TableHead className="w-20">Tipo</TableHead>
                          <TableHead>Coluna</TableHead>
                          <TableHead>Problema</TableHead>
                          <TableHead>Valor</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {validacao.ocorrencias.map((o, i) => (
                          <TableRow key={i}>
                            <TableCell>{o.linha}</TableCell>
                            <TableCell>{badgeSeveridade(o.severidade)}</TableCell>
                            <TableCell className="text-muted-foreground">{o.coluna ?? "—"}</TableCell>
                            <TableCell>{o.mensagem}</TableCell>
                            <TableCell className="text-muted-foreground max-w-40 truncate">
                              {o.valorBruto ?? "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}

                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setStep(2)}>
                    <ArrowLeft className="h-4 w-4 mr-2" /> Ajustar mapeamento
                  </Button>
                  <Button onClick={carregar} disabled={carregando || validacao.linhasValidas === 0}>
                    {carregando && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                    Importar {validacao.linhasValidas} linhas
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Passo 4 — Resultado */}
          {step === 4 && carga && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" /> 4. Importação concluída
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 max-w-3xl">
                  {[
                    ["Inseridos", carga.inseridos],
                    ["Atualizados", carga.atualizados],
                    ["Pulados", carga.pulados],
                    ["Rejeitados", carga.rejeitados],
                    ["Avisos", carga.avisos],
                  ].map(([rotulo, valor]) => (
                    <div key={rotulo as string} className="border rounded-md p-3 text-center">
                      <div className="text-2xl font-bold">{valor}</div>
                      <div className="text-xs text-muted-foreground">{rotulo}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  {(carga.rejeitados > 0 || carga.avisos > 0) && (
                    <Button variant="outline" onClick={() => baixarErros(carga.jobId)}>
                      <Download className="h-4 w-4 mr-2" /> Baixar relatório de ocorrências
                    </Button>
                  )}
                  <Button onClick={reiniciar}>Nova importação</Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ═══ Aba HISTÓRICO ═══ */}
        <TabsContent value="historico">
          <Card>
            <CardHeader>
              <CardTitle>Importações realizadas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap items-end gap-3">
                <div className="w-56">
                  <Label className="text-xs text-muted-foreground">Tipo</Label>
                  <Select value={filtroEntidade} onValueChange={setFiltroEntidade}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TODOS}>Todos os tipos</SelectItem>
                      {Object.entries(ROTULO_ENTIDADE).map(([valor, rotulo]) => (
                        <SelectItem key={valor} value={valor}>{rotulo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-56">
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  <Select value={filtroStatus} onValueChange={setFiltroStatus}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={TODOS}>Todos os status</SelectItem>
                      {Object.entries(ROTULO_STATUS).map(([valor, rotulo]) => (
                        <SelectItem key={valor} value={valor}>{rotulo}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {carregandoHistorico && <Loader2 className="h-5 w-5 animate-spin mb-2" />}
              </div>
              {!carregandoHistorico && (historico ?? []).length === 0 && (
                <p className="text-muted-foreground text-sm">
                  {filtrandoHistorico ? "Nenhuma importação com estes filtros." : "Nenhuma importação ainda."}
                </p>
              )}
              {(historico ?? []).length > 0 && (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Protocolo</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Arquivo</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Linhas</TableHead>
                        <TableHead className="text-right">Inseridos</TableHead>
                        <TableHead className="text-right">Atualizados</TableHead>
                        <TableHead className="text-right">Pulados</TableHead>
                        <TableHead className="text-right">Rejeitados</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(historico ?? []).map((j) => (
                        <TableRow key={j.id}>
                          <TableCell>{j.id}</TableCell>
                          <TableCell>{new Date(j.dtCriacao).toLocaleString("pt-BR")}</TableCell>
                          <TableCell>{ROTULO_ENTIDADE[j.entidade] ?? j.entidade}</TableCell>
                          <TableCell className="max-w-48 truncate">{j.nomeArquivo}</TableCell>
                          <TableCell>
                            <Badge variant={j.status === "Concluido" ? "default"
                              : j.status === "Falha" ? "destructive" : "outline"}>
                              {ROTULO_STATUS[j.status] ?? j.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">{j.totalLinhas}</TableCell>
                          <TableCell className="text-right">{j.inseridos}</TableCell>
                          <TableCell className="text-right">{j.atualizados}</TableCell>
                          <TableCell className="text-right">{j.pulados}</TableCell>
                          <TableCell className="text-right">{j.rejeitados}</TableCell>
                          <TableCell>
                            {(j.rejeitados > 0 || j.avisos > 0) && (
                              <Button variant="ghost" size="sm" onClick={() => baixarErros(j.id)}
                                title="Baixar relatório de ocorrências">
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
              <PaginacaoRodape {...pagHistorico.rodape(historicoResp)} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
